import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

const CATEGORY_LABELS = {
  music_services: "Music Services",
  graphic_design: "Graphic Design",
  tax_legal: "Tax / Legal",
  branding: "Branding",
  marketing: "Marketing",
  merch: "Merch",
  podcast: "Podcast",
  education: "Education",
  other: "Other",
};

/**
 * Featured businesses carousel — 3 random published businesses per session.
 * - Shows 3 cards on desktop (lg:grid-cols-3) and a horizontally scrollable
 *   strip on mobile.
 * - Hides itself entirely when the API returns no businesses (or fails).
 * - Memoizes the picks per browser session via sessionStorage so the same
 *   user sees a stable selection across pages within the session, but a fresh
 *   set on the next visit.
 */
const FeaturedBusinessesCarousel = ({
  variant = "explore", // 'landing' | 'explore'
  limit = 3,
}) => {
  const [businesses, setBusinesses] = useState(null); // null=loading, []=empty

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `rgm_featured_businesses_v1`;
    // Per-session memoization
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setBusinesses(parsed);
          return () => { cancelled = true; };
        }
      }
    } catch { /* ignore */ }

    (async () => {
      try {
        const r = await api.get("/businesses/featured", { params: { limit } });
        const list = r.data?.businesses || [];
        if (cancelled) return;
        setBusinesses(list);
        if (list.length > 0) {
          try { sessionStorage.setItem(cacheKey, JSON.stringify(list)); } catch { /* ignore */ }
        }
      } catch {
        if (!cancelled) setBusinesses([]); // fail-silent
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  // Hide entirely while loading (avoid layout shift) and when empty
  if (!businesses || businesses.length === 0) return null;

  const isLanding = variant === "landing";
  return (
    <section
      className={isLanding ? "py-16 bg-[#0a0a0f]" : "py-10"}
      data-testid="featured-businesses-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
          <div>
            <h2 className={`${isLanding ? "text-2xl sm:text-4xl font-black" : "text-xl sm:text-2xl font-bold"} text-white flex items-center gap-2`}>
              <span>💼</span> Featured Businesses
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              Real services from RGM creators — book directly, no middlemen.
            </p>
          </div>
          <Link to="/business" data-testid="featured-businesses-all-link">
            <Button variant="outline" className="text-white border-purple-500/50 whitespace-nowrap">
              Browse Hub →
            </Button>
          </Link>
        </div>

        {/* Mobile: horizontal scroll. Desktop: 3-up grid. */}
        <div
          className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory sm:snap-none pb-2"
          data-testid="featured-businesses-list"
        >
          {businesses.map((b) => (
            <Card
              key={b.business_id}
              className="min-w-[260px] sm:min-w-0 snap-center bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/60 transition-all flex flex-col overflow-hidden"
              data-testid={`featured-biz-${b.business_id}`}
            >
              <div className="aspect-[16/9] relative overflow-hidden bg-gradient-to-br from-purple-900/40 to-pink-900/40">
                {b.cover_image ? (
                  <img
                    src={optimizeImage(b.cover_image, 'card')}
                    alt={b.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : b.logo ? (
                  <img src={b.logo} alt={b.name} className="w-full h-full object-contain bg-black/40" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-5xl">💼</div>
                )}
                <span className="absolute top-2 left-2 text-[11px] px-2 py-0.5 rounded-full bg-black/60 text-purple-200 border border-purple-500/40">
                  {CATEGORY_LABELS[b.category] || (b.category || "Other").replace("_", " ")}
                </span>
                {b.is_spotlight && (
                  <span
                    className="absolute top-2 right-2 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-100 border border-amber-400/50 font-semibold"
                    data-testid={`spotlight-flag-${b.business_id}`}
                  >
                    ✨ Featured Business Spotlight
                  </span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                <h3 className="text-white font-bold leading-tight truncate">{b.name}</h3>
                <p className="text-gray-400 text-sm line-clamp-2">
                  {b.description || <span className="italic text-gray-600">No description yet.</span>}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                  <img
                    src={getProfilePhotoUrl(b.owner || {})}
                    alt=""
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="truncate flex-1">{b.owner?.artist_name}</span>
                  {b.service_count > 0 && (
                    <span className="text-purple-300">🛠 {b.service_count}</span>
                  )}
                </div>
                <Link
                  to={`/business/${b.business_id}`}
                  className="mt-auto pt-2"
                  data-testid={`featured-biz-cta-${b.business_id}`}
                >
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                    View Business
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedBusinessesCarousel;
